import { Badge, Button, ErrorBanner, Spinner } from '@/components/ui';
import { cn } from '@/lib/utils';

export interface ArchivableColumn<T> {
  /** Visible label rendered to screen readers (and as a header on md+ if `withHeaderRow`). */
  header: string;
  /** Renders the cell for a row. */
  cell: (item: T) => React.ReactNode;
  /** Optional CSS for the cell wrapper. */
  className?: string;
}

interface ArchivableListShellProps<T> {
  items: T[];
  isLoading: boolean;
  error: string | null;
  /** Used to determine whether to show ARCHIVED marker and which action button (Archive / Unarchive). */
  isArchived: (item: T) => boolean;
  /** Stable React key per row. */
  rowKey: (item: T) => string;
  /** Cells to render for each row, in display order. */
  columns: ArchivableColumn<T>[];
  showArchived: boolean;
  onShowArchivedChange: (next: boolean) => void;
  onCreate: () => void;
  onEdit: (item: T) => void;
  onArchiveToggle: (item: T) => void;
  /** Label for the create button (e.g. "Create news item"). */
  createButtonLabel: string;
  /** Empty-state message rendered when items.length === 0 and not loading/erroring. */
  emptyMessage: string;
}

/**
 * Reusable admin-list shell for archivable resources (news, upcoming features).
 *
 * Owns the chrome (header with Show-archived toggle + create button, loading /
 * error / empty states, ARCHIVED marker, edit + archive/unarchive buttons) but
 * is otherwise presentational — the parent fetches data and handles
 * showArchived state + mutation callbacks.
 */
export function ArchivableListShell<T>({
  items,
  isLoading,
  error,
  isArchived,
  rowKey,
  columns,
  showArchived,
  onShowArchivedChange,
  onCreate,
  onEdit,
  onArchiveToggle,
  createButtonLabel,
  emptyMessage,
}: Readonly<ArchivableListShellProps<T>>) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => onShowArchivedChange(e.target.checked)}
            className="h-4 w-4 rounded border-input"
          />
          {' '}
          Show archived
        </label>
        <Button onClick={onCreate}>{createButtonLabel}</Button>
      </div>

      {error && <ErrorBanner message={error} />}

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner /> Loading…
        </div>
      )}

      {!isLoading && !error && items.length === 0 && (
        <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          {emptyMessage}
        </p>
      )}

      {!isLoading && !error && items.length > 0 && (
        <ul className="divide-y divide-border rounded-md border border-border">
          {items.map((item) => {
            const archived = isArchived(item);
            return (
              <li
                key={rowKey(item)}
                className={cn(
                  'flex flex-wrap items-center gap-3 px-4 py-3',
                  archived && 'bg-muted/30',
                )}
              >
                {columns.map((col) => (
                  <div key={col.header} className={cn('min-w-0 flex-1', col.className)}>
                    <span className="sr-only">{col.header}: </span>
                    {col.cell(item)}
                  </div>
                ))}
                {archived && (
                  <Badge variant="secondary" className="shrink-0 uppercase tracking-wider">
                    Archived
                  </Badge>
                )}
                <div className="flex shrink-0 items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => onEdit(item)}>
                    Edit
                  </Button>
                  <Button
                    variant={archived ? 'secondary' : 'outline'}
                    size="sm"
                    onClick={() => onArchiveToggle(item)}
                  >
                    {archived ? 'Unarchive' : 'Archive'}
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
